import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, CalendarRange } from 'lucide-react';
import {
  currentSchoolYear, gradeForYear, gradeLabel,
  schoolYearRange, kStartYearFromGrade,
} from '../../utils/schoolYears';

const FONT_H = "'Lora', Georgia, serif";
const FONT_B = "'Source Sans 3', sans-serif";

const GRADES = [
  { value: null, label: 'None', sub: 'not yet' },
  { value: 0, label: 'K' },
  ...Array.from({ length: 12 }, (_, i) => {
    const g = i + 1;
    const suffixes = { 1: 'st', 2: 'nd', 3: 'rd' };
    return { value: g, label: `${g}${suffixes[g] || 'th'}` };
  }),
];

export default function ChangeGradeModal({ isOpen, onClose, onSave, firstName, gender, computedGrade, currentKStartYear, titleOverride }) {
  const curSY = currentSchoolYear();
  const [picked, setPicked] = useState(computedGrade);
  const dialogRef = useRef(null);

  useEffect(() => {
    if (isOpen) setPicked(computedGrade);
  }, [isOpen, computedGrade]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const kStart = picked != null ? kStartYearFromGrade(picked) : null;
  const kEnd = kStart != null ? kStart + 12 : null;

  const summaryText = picked != null
    ? `${gradeLabel(0)} ${schoolYearRange(kStart)} \u00B7 ${gradeLabel(12)} ${schoolYearRange(kEnd)}. ${firstName} is in ${gradeLabel(picked)} this year.`
    : `${firstName} isn\u2019t in school yet. ${gender === 'female' ? 'Her' : gender === 'male' ? 'His' : 'Their'} stories will be grouped by year of age until ${gender === 'female' ? 'she' : gender === 'male' ? 'he' : 'they'} start${gender === 'female' || gender === 'male' ? 's' : ''}.`;

  const handleSave = () => {
    if (picked != null) {
      onSave({ kStartYear: kStartYearFromGrade(picked), notInSchool: false });
    } else {
      onSave({ kStartYear: null, notInSchool: true });
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(44,47,38,.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Which grade is ${firstName} in this year?`}
        style={{
          background: '#fff', borderRadius: 16,
          boxShadow: '0 16px 40px rgba(20,24,14,.3)',
          width: '100%', maxWidth: 436, padding: '28px 28px 24px',
          position: 'relative', maxHeight: '90vh', overflowY: 'auto',
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute', top: 12, right: 12,
            width: 44, height: 44, border: 'none', background: 'none',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: 8,
          }}
        >
          <X size={24} color="#2C2F26" />
        </button>

        {/* Title */}
        <h2 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 23, color: '#2C2F26', margin: '0 0 6px' }}>
          {titleOverride || `Which grade is ${firstName} in this year?`}
        </h2>
        <p style={{ fontFamily: FONT_B, fontSize: 15.5, color: '#3F4436', margin: '0 0 20px', lineHeight: 1.5 }}>
          We guessed from {gender === 'female' ? 'her' : gender === 'male' ? 'his' : 'their'} birthday. Pick {gender === 'female' ? 'her' : gender === 'male' ? 'his' : 'their'} real grade if {gender === 'female' ? 'she' : gender === 'male' ? 'he' : 'they'} started early or late, or repeated or skipped a year.
        </p>

        {/* Eyebrow */}
        <p style={{
          fontFamily: FONT_B, fontSize: 14, fontWeight: 700, color: '#4A4F40',
          letterSpacing: '.04em', margin: '0 0 10px', textTransform: 'uppercase',
        }}>
          SCHOOL YEAR {schoolYearRange(curSY)}
        </p>

        {/* Grade grid */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8,
          marginBottom: 18,
        }}>
          {GRADES.map((g) => {
            const isSelected = picked === g.value;
            const isGuess = g.value === computedGrade && !isSelected;
            return (
              <button
                key={g.value ?? 'none'}
                onClick={() => setPicked(g.value)}
                style={{
                  minHeight: 52, borderRadius: 12,
                  fontFamily: FONT_B, fontSize: 16, fontWeight: 700,
                  cursor: 'pointer', border: 'none', position: 'relative',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  background: isSelected ? '#3E4F22' : '#fff',
                  color: isSelected ? '#fff' : '#2C2F26',
                  boxShadow: isSelected
                    ? 'none'
                    : isGuess
                      ? 'inset 0 0 0 2px #9A9F8A'
                      : 'inset 0 0 0 2px #D6D1BD',
                }}
              >
                <span>{g.label}</span>
                {isGuess && (
                  <span style={{ fontSize: 11.5, fontWeight: 600, color: '#7F846F', marginTop: 1 }}>our guess</span>
                )}
                {g.value === null && !isSelected && (
                  <span style={{ fontSize: 11.5, fontWeight: 600, color: '#7F846F', marginTop: 1 }}>{g.sub}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Summary box */}
        <div style={{
          background: '#F1F5E8', borderRadius: 12, padding: '14px 16px',
          display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 20,
        }}>
          <CalendarRange size={20} color="#3E4F22" style={{ marginTop: 2, flexShrink: 0 }} />
          <p style={{ fontFamily: FONT_B, fontSize: 15, color: '#2C2F26', margin: 0, lineHeight: 1.5 }}>
            {summaryText}
          </p>
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, minHeight: 56, borderRadius: 14,
              border: '2px solid #9A9F8A', background: '#fff',
              fontFamily: FONT_B, fontSize: 17, fontWeight: 700, color: '#2C2F26',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            style={{
              flex: 1.5, minHeight: 56, borderRadius: 14,
              border: 'none', background: '#3E4F22',
              fontFamily: FONT_B, fontSize: 17, fontWeight: 700, color: '#fff',
              cursor: 'pointer',
            }}
          >
            Save grade
          </button>
        </div>
      </div>
    </div>
  );
}
